import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { searchClients } from '@/features/clients/api/clients';
import { getPeople } from '@/features/people/api/people';
import {
  createDashboardMessage,
  getDashboardMessages,
} from '@/features/settings/api/dashboardMessages';

export function TechnicianDashboardSettingsPage() {
  const [tab, setTab] = useState(0);
  const [message, setMessage] = useState('');
  const [userId, setUserId] = useState(0);
  const [clientId, setClientId] = useState(0);
  const [expiresAt, setExpiresAt] = useState('');
  const qc = useQueryClient();
  const messages = useQuery({ queryKey: ['dashboard-messages'], queryFn: getDashboardMessages });
  const techs = useQuery({
    queryKey: ['people', 'technicians'],
    queryFn: () => getPeople('technicians'),
  });
  const clients = useQuery({
    queryKey: ['clients', 'dashboard-options'],
    queryFn: () => searchClients(),
  });
  const save = useMutation({
    mutationFn: () =>
      createDashboardMessage({
        message: message.trim(),
        userId: userId || null,
        clientId: clientId || null,
        expiresAt: expiresAt || null,
      }),
    onSuccess: async () => {
      setMessage('');
      await qc.invalidateQueries({ queryKey: ['dashboard-messages'] });
    },
  });
  const rows = (messages.data ?? []).filter((x) => (tab === 0 ? !x.expired : x.expired));
  const techName = (id?: number | null) =>
    techs.data?.find((x) => x.id === id)?.name ?? (id ? 'Unknown' : 'All technicians');
  const clientName = (id?: number | null) =>
    clients.data?.find((x) => x.id === id)?.name ?? (id ? 'Unknown' : 'No client');
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h1">Technician In-App Dashboard</Typography>
          <Typography color="text.secondary">
            Publish targeted messages to technicians in the mobile application.
          </Typography>
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 2 }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          New dashboard message
        </Typography>
        <Stack spacing={2}>
          <TextField
            multiline
            minRows={3}
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField
              select
              fullWidth
              label="Technician"
              value={userId}
              onChange={(e) => setUserId(Number(e.target.value))}
            >
              <MenuItem value={0}>All technicians</MenuItem>
              {(techs.data ?? []).map((x) => (
                <MenuItem key={x.id} value={x.id}>
                  {x.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Client"
              value={clientId}
              onChange={(e) => setClientId(Number(e.target.value))}
            >
              <MenuItem value={0}>No client</MenuItem>
              {(clients.data ?? []).map((x) => (
                <MenuItem key={x.id} value={x.id}>
                  {x.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="date"
              label="Expires"
              slotProps={{ inputLabel: { shrink: true } }}
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </Stack>
          {save.isError && <Alert severity="error">The message could not be published.</Alert>}
          {save.isSuccess && <Alert severity="success">Message published.</Alert>}
          <Button
            variant="contained"
            startIcon={<SendOutlinedIcon />}
            disabled={!message.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Publish message
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 1.5 }}>
        <Tabs value={tab} onChange={(_, v: number) => setTab(v)}>
          <Tab label="Active" />
          <Tab label="Expired" />
        </Tabs>
        {messages.isError && <Alert severity="error">Messages could not be loaded.</Alert>}
        {rows.map((x) => (
          <Box key={x.id} sx={{ p: 2, borderBottom: '1px solid #edebe9' }}>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip size="small" label={techName(x.user_id)} color="primary" />
              <Chip size="small" label={clientName(x.client_id)} />
              {x.expires_at && (
                <Chip
                  size="small"
                  label={`Expires ${new Date(x.expires_at).toLocaleDateString('en-GB')}`}
                />
              )}
            </Stack>
            <Typography sx={{ mt: 1 }}>{x.message}</Typography>
            <Typography variant="caption" color="text.secondary">
              Published {new Date(x.created_at).toLocaleString('en-GB')}
            </Typography>
          </Box>
        ))}
        {!rows.length && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No {tab === 0 ? 'active' : 'expired'} messages.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
